import { supabase } from '@/lib/supabaseClient';

/**
 * Service for managing screen permissions
 */
export interface Screen {
  id: string;
  screen_key: string;
  display_name: string;
  description?: string;
  icon?: string;
  group_name?: string;
  sort_order?: number;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface ScreenPermission {
  id: string;
  user_id: string;
  screen_id: string;
  can_view: boolean;
  can_edit: boolean;
  created_at?: string;
  updated_at?: string;
}

/**
 * Gets all screens in the system
 */
export async function getScreens(): Promise<Screen[]> {
  try {
    const { data, error } = await supabase
      .from('screens')
      .select('*')
      .order('display_name');
    
    if (error) {
      console.error('Error fetching screens:', error);
      return [];
    }
    
    return (data as unknown) as Screen[];
  } catch (err) {
    console.error('Unexpected error fetching screens:', err);
    return [];
  }
}

/**
 * Gets permissions for a specific user
 * @param userId User ID to get permissions for
 */
export async function getUserPermissions(userId: string): Promise<ScreenPermission[]> {
  try {
    const { data, error } = await supabase
      .from('screen_permissions')
      .select(`
        id,
        user_id,
        screen_id,
        can_view,
        can_edit,
        created_at,
        updated_at
      `)
      .eq('user_id', userId);
    
    if (error) {
      console.error('Error fetching user permissions:', error);
      return [];
    }
    
    return data as ScreenPermission[];
  } catch (err) {
    console.error('Unexpected error fetching user permissions:', err);
    return [];
  }
}

/**
 * Gets screens with their permission status for a specific user
 * @param userId User ID to get screen permissions for
 */
export async function getUserScreensWithPermissions(userId: string): Promise<
  Array<Screen & { permissions: Omit<ScreenPermission, 'id' | 'user_id' | 'screen_id' | 'created_at' | 'updated_at'> }>
> {
  try {
    // First get all screens
    const screens = await getScreens();
    
    // Then get user permissions
    const userPermissions = await getUserPermissions(userId);
    
    // Map permissions to screens
    return screens.map(screen => {
      const permission = userPermissions.find(p => p.screen_id === screen.screen_key);
      
      return {
        ...screen,
        permissions: {
          can_view: permission?.can_view || false,
          can_edit: permission?.can_edit || false
        }
      };
    });
  } catch (err) {
    console.error('Error fetching screens with permissions:', err);
    return [];
  }
}

/**
 * Checks if a user has permission to view a specific screen
 * @param userId User ID to check
 * @param screenKey Key of the screen to check
 */
export async function canUserViewScreen(userId: string, screenKey: string): Promise<boolean> {
  try {
    console.log(`Checking permission for user ${userId} to view screen ${screenKey}`);
    
    // בדיקה ישירה אם יש למשתמש הרשאה למסך לפי screen_key
    const { data: permissionData, error: permissionError } = await supabase
      .from('screen_permissions')
      .select('can_view')
      .eq('user_id', userId)
      .eq('screen_id', screenKey)  // השתמש ב-screenKey ישירות במקום id המסך
      .single();
    
    if (permissionError) {
      console.error('Error checking permission:', permissionError);
      
      // Get all permissions for this user to help debug
      const { data: allPermissions } = await supabase
        .from('screen_permissions')
        .select('screen_id, can_view')
        .eq('user_id', userId);
        
      console.log('All available permissions for user:', allPermissions);
      return false;
    }
    
    const hasPermission = Boolean(permissionData?.can_view) || false;
    console.log(`Permission result for ${screenKey}: ${hasPermission}`);
    return hasPermission;
  } catch (err) {
    console.error('Unexpected error checking screen permission:', err);
    return false;
  }
}

/**
 * Sets permissions for a user on a specific screen
 * @param userId User ID to set permissions for
 * @param screenId Screen ID to set permissions on
 * @param permissions Permission flags to set
 */
export async function setScreenPermission(
  userId: string,
  screenId: string,
  permissions: { can_view: boolean; can_edit: boolean; /* can_delete: boolean */ }
): Promise<boolean> {
  try {
    // Check if permission already exists
    const { data: existingData, error: existingError } = await supabase
      .from('screen_permissions')
      .select('id')
      .eq('user_id', userId)
      .eq('screen_id', screenId)
      .single();
    
    if (existingError && existingError.code !== 'PGRST116') { // Not found is ok
      console.error('Error checking existing permission:', existingError);
      return false;
    }
    
    if (existingData) {
      // Update existing permission
      const { error } = await supabase
        .from('screen_permissions')
        .update({
          can_view: permissions.can_view,
          can_edit: permissions.can_edit,
          updated_at: new Date().toISOString()
        })
        .eq('id', existingData.id);
      
      if (error) {
        console.error('Error updating permission:', error);
        return false;
      }
    } else {
      // Insert new permission
      const { error } = await supabase
        .from('screen_permissions')
        .insert({
          user_id: userId,
          screen_id: screenId,
          can_view: permissions.can_view,
          can_edit: permissions.can_edit
        });
      
      if (error) {
        console.error('Error creating permission:', error);
        return false;
      }
    }
    
    return true;
  } catch (err) {
    console.error('Unexpected error setting screen permission:', err);
    return false;
  }
}

/**
 * מוסיף הרשאות צפייה ועריכה לכל המסכים עבור משתמש מסוים
 * @param userId מזהה המשתמש שעבורו להוסיף הרשאות
 */
export async function addAllScreenPermissions(userId: string): Promise<boolean> {
  try {
    console.log(`מתחיל להוסיף הרשאות למשתמש ${userId}...`);
    
    // 1. קבל את כל המסכים
    const { data: screens, error: screensError } = await supabase
      .from('screens')
      .select('id, screen_key, display_name');
    
    if (screensError || !screens) {
      console.error('שגיאה בקבלת מסכים:', screensError);
      return false;
    }
    
    // 2. בדוק אילו הרשאות כבר קיימות
    const { data: existingPermissions, error: permissionsError } = await supabase
      .from('screen_permissions')
      .select('id, screen_id')
      .eq('user_id', userId);
    
    if (permissionsError || !existingPermissions) {
      console.error('שגיאה בקבלת הרשאות קיימות:', permissionsError);
      return false;
    }
    
    const existingScreenKeys = existingPermissions.map(p => p.screen_id);
    
    // 3. מצא מסכים חסרים
    const missingScreens = screens.filter(screen => !existingScreenKeys.includes(screen.screen_key));
    
    if (missingScreens.length === 0) {
      console.log('למשתמש כבר יש הרשאות לכל המסכים!');
      return true;
    }
    
    // 4. הוסף הרשאות למסכים החסרים
    const permissionsToInsert = missingScreens.map(screen => ({
      user_id: userId,
      screen_id: screen.screen_key,
      can_view: true,
      can_edit: true
    }));
    
    const { error: insertError } = await supabase
      .from('screen_permissions')
      .insert(permissionsToInsert);
    
    if (insertError) {
      console.error('שגיאה בהוספת הרשאות:', insertError);
      return false;
    }
    
    console.log(`הוספו בהצלחה הרשאות ל-${permissionsToInsert.length} מסכים!`);
    return true;
  } catch (err) {
    console.error('שגיאה לא צפויה בהוספת הרשאות:', err);
    return false;
  }
}

// Export a default object with all functions for easy importing
export default {
  getScreens,
  getUserPermissions,
  getUserScreensWithPermissions,
  canUserViewScreen,
  setScreenPermission,
  addAllScreenPermissions
}; 